import { productSearchApi, ProductSearchApiError } from "../api/client";
import type { WishlistBatch, WishlistListResponse, WishlistMutation, WishlistSummary } from "./contracts";

const key = () => crypto.randomUUID();

async function write<T>(path: string, method: "POST" | "DELETE", body?: unknown, timeoutMs = 12_000): Promise<T> {
  const requestKey = key();
  const init: RequestInit = { method, headers: { "Idempotency-Key": requestKey, ...(body === undefined ? {} : { "Content-Type": "application/json" }) }, ...(body === undefined ? {} : { body: JSON.stringify(body) }) };
  try {
    return await productSearchApi.wishlistRequest<T>(path, init, timeoutMs);
  } catch (error) {
    if (!(error instanceof ProductSearchApiError) || !["request_timeout", "network_error"].includes(error.code)) throw error;
    for (const delay of [400, 1000, 1800]) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      try {
        const status = await productSearchApi.wishlistRequest<{state:string;httpStatus:number|null;result:T|null}>(`/wishlist/requests/${requestKey}`);
        if (status.state === "succeeded" && status.result) return status.result;
        if (status.state === "failed" && status.result) throw new ProductSearchApiError("这次操作未完成，请明确重试", "operation_failed", true, status.httpStatus || 503);
      } catch (statusError) {
        if (statusError instanceof ProductSearchApiError && ![0, 404, 500, 502, 503, 504].includes(statusError.status)) throw statusError;
      }
    }
    throw new ProductSearchApiError("操作仍在处理，请稍后刷新心愿单确认结果", "operation_processing", true, 202, requestKey);
  }
}

export const wishlistApi = {
  list(cursor = "", limit = 20) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return productSearchApi.wishlistRequest<WishlistListResponse>(`/wishlist?${params}`);
  },
  summary() {
    return productSearchApi.wishlistRequest<WishlistSummary>("/wishlist/summary");
  },
  selection(listRevision: number) {
    return productSearchApi.wishlistRequest<{selectedIds:string[];selectedCount:number;total:number;hasMore:boolean;listRevision:number}>(`/wishlist/selection?listRevision=${listRevision}`);
  },
  createSearch(query: string) {
    return write<WishlistBatch>("/wishlist/search-sessions", "POST", { query }, 65_000);
  },
  nextBatch(sessionId: string, previousBatchId: string) {
    return write<WishlistBatch>(`/wishlist/search-sessions/${encodeURIComponent(sessionId)}/batches`, "POST", { previousBatchId }, 65_000);
  },
  addCandidate(batch: WishlistBatch, candidateId: string) {
    return write<WishlistMutation>("/wishlist/items", "POST", { source: "search_confirm", sessionId: batch.sessionId, batchId: batch.batchId, candidateId });
  },
  addPerfume(productKey: string) {
    return write<WishlistMutation>("/wishlist/items", "POST", { source: "detail_perfume", productKey });
  },
  addDetail(productKey: string, skuKey: string) {
    return write<WishlistMutation>("/wishlist/items", "POST", { source: "detail_out_of_stock", productKey, skuKey });
  },
  remove(itemId: string) {
    return write<WishlistMutation>(`/wishlist/items/${encodeURIComponent(itemId)}`, "DELETE");
  },
  batchDelete(itemIds: string[]) {
    return write<WishlistMutation>("/wishlist/items/batch-delete", "POST", { itemIds });
  },
};
